export type InsuranceProductPolicyItemConfiguration = {
  isHidden: boolean
  canBeUpdated: boolean
  canBeSelected: boolean
  limitOfIndemnityControl?: 'radios' | 'dropdown'
}

export type InsuranceProductPolicyItemConfigurationContext = 'selfonboarding' | 'backoffice'

/**
 * Resolves the effective configuration of a policy item from a dynamic state and the current
 * context (self-onboarding or backoffice).
 */
export type InsuranceProductPolicyItemConfigurationResolver<STATE extends Record<string, unknown>> = (args: {
  state: STATE
  context: InsuranceProductPolicyItemConfigurationContext
}) => InsuranceProductPolicyItemConfiguration

export type PolicyItemConfigurationResolverParams<STATE extends Record<string, unknown>> = {
  selfOnboardingConfiguration: InsuranceProductPolicyItemConfigurationResolver<STATE>
  backofficeConfiguration: InsuranceProductPolicyItemConfigurationResolver<STATE>
}

/**
 * Builds a policy item configuration resolver from self-onboarding and backoffice static configs.
 */
export function createPolicyItemConfigurationResolver<STATE extends Record<string, unknown>>({
  selfOnboardingConfiguration,
  backofficeConfiguration,
}: PolicyItemConfigurationResolverParams<STATE>): InsuranceProductPolicyItemConfigurationResolver<STATE> {
  return (args) => {
    switch (args.context) {
      case 'selfonboarding':
        return selfOnboardingConfiguration(args)
      case 'backoffice':
        return backofficeConfiguration(args)
    }
  }
}

export type PolicyItemConfigurationResolveArgs<STATE extends Record<string, unknown>> = {
  state: STATE
  context: InsuranceProductPolicyItemConfigurationContext
}

type PolicyItemConfigurationState = Record<string, unknown>

export function resolvePolicyItemConfiguration(
  editConfiguration: InsuranceProductPolicyItemConfigurationResolver<PolicyItemConfigurationState>,
  args: PolicyItemConfigurationResolveArgs<PolicyItemConfigurationState>,
): InsuranceProductPolicyItemConfiguration {
  return editConfiguration(args)
}
